import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash-es';
import { catchError, of, tap } from 'rxjs';
import { SpecLine, SpecificationType } from 'src/app/features/asset-management/models/SpecLine';

import { Specification } from 'src/app/features/asset-management/models/Specification';
import { CamSpec } from 'src/app/features/search-cam-spec/model/camspec.model';
import { MRT } from '../../features/asset-management/models/MRT';
import { AssetManagementService } from '../../features/asset-management/services/asset-management.service';
import { prepareSpecificationLineLabels } from 'src/app/features/cam-specification/utils/SpecificationUtils';

export class UpdateCurrentSpecificationIdAction {
  static readonly type = '[Asset Management] Update Current Specification Id';
  constructor(public specificationId: string) {}
}

export class UpdateCurrentCustomerAssetsAction {
  static readonly type = '[Asset Management] Update Current Customer Assets';
  constructor(public customerId: string) {}
}

export class FetchMrtSpecificationsAction {
  static readonly type = '[Asset Management] Fetch MRT Specifications';
}

export class ResetAssetManagamentStateAction {
  static readonly type = '[Asset Management] Asset Management State Reset';
}

export class UpdateEditingModeAction {
  static readonly type = '[Asset Management] Update Editing Mode';
  constructor(public editMode: boolean) {}
}

export class UpdateSpecificationLineAction {
  static readonly type = '[Asset Management] Update Specification Line';
  constructor(public updatedSpecificationLine: SpecLine) {}
}

export class UpdateCurrentSpecificationAction {
  static readonly type = '[Asset Management] Update Current Specification';
  constructor(public specification: Specification) {}
}

export class FetchCurrentSpecificationAction {
  static readonly type = '[Asset Management] Fetch Current Specification';
  constructor(public specificationId: string) {}
}

export class UpdateCurrentPaginatedSpecificationsAction {
  static readonly type = '[Asset Management] Update Current Paginated Specifications';
  constructor(public specifications: CamSpec[]) {}
}

export class AssetManagementStateModel {
  loading = true;
  editing = false;
  currentCustomerCode: string = undefined;
  currentSpecificationId: string = undefined;
  specificationsMap: Map<string, SpecLine> = new Map();
  specificationsList: Array<SpecLine> = [];
  mrtSpecifications: Map<string, MRT> = new Map<string, MRT>();
  currentSpecification: Specification;
  currentPaginatedSpecifications: CamSpec[];
}

const defaultState = {
  loading: true,
  editing: false,
  currentCustomerCode: undefined as string,
  currentSpecificationId: undefined as string,
  specificationsMap: new Map<string, SpecLine>(),
  specificationsList: [] as SpecLine[],
  mrtSpecifications: new Map<string, MRT>(),
  currentSpecification: undefined as Specification,
  currentPaginatedSpecifications: [] as CamSpec[]
};

const getDefaultState = () => {
  return { ...defaultState };
};

@State<AssetManagementStateModel>({
  name: 'currentCustomerAssets',
  defaults: getDefaultState()
})
@Injectable()
export class AssetManagementState {
  private readonly assetManagementService = inject(AssetManagementService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  constructor() {}

  @Action(UpdateCurrentSpecificationIdAction)
  setCurrentSpecificationId(
    { patchState }: StateContext<AssetManagementStateModel>,
    { specificationId }: UpdateCurrentSpecificationIdAction
  ) {
    patchState({ currentSpecificationId: specificationId });
  }

  @Action(UpdateCurrentCustomerAssetsAction)
  setCurrentCustomerAssets({ patchState }: StateContext<AssetManagementStateModel>, { customerId }: UpdateCurrentCustomerAssetsAction) {
    patchState({ loading: true });
    let { dealerId, customerCode, specificationId } = this.store.selectSnapshot(
      (state) => state.router.state.root.firstChild.firstChild.params
    );

    if (dealerId && customerCode) {
      return this.assetManagementService.getSpecificationLines(customerId).pipe(
        tap((response) => {
          const specLines: SpecLine[] = this.processSpecifications(response.dataList);
          if (specLines.length > 0 && !specificationId) {
            specificationId = specLines[0].id;
          }

          patchState({
            currentCustomerCode: customerCode,
            currentSpecificationId: specificationId,
            specificationsList: specLines,
            specificationsMap: _.reduce(
              response.dataList,
              (acc: Map<string, SpecLine>, item: SpecLine) => {
                acc.set(item.id, item);
                return acc;
              },
              new Map<string, SpecLine>()
            ),
            loading: false
          });
        }),

        catchError((error) => {
          patchState(getDefaultState());
          throw error;
        })
      );
    } else {
      patchState(getDefaultState());
    }
  }

  @Action(UpdateSpecificationLineAction)
  setSpecificationLine(
    { patchState, getState }: StateContext<AssetManagementStateModel>,
    { updatedSpecificationLine }: UpdateSpecificationLineAction
  ) {
    const currentSpecificationId = getState().currentSpecificationId;
    const index = getState().specificationsList.findIndex((s) => s.id === currentSpecificationId);
    const specLines = getState().specificationsList;
    const updatedSpecLines = [...specLines];
    updatedSpecLines[index] = { ...updatedSpecificationLine };
    patchState({
      specificationsList: updatedSpecLines,
      specificationsMap: getState().specificationsMap.set(currentSpecificationId, updatedSpecificationLine)
    });
  }

  @Action(UpdateEditingModeAction)
  setEditingMode({ patchState }: StateContext<AssetManagementStateModel>, { editMode }: UpdateEditingModeAction) {
    patchState({ editing: editMode });
  }

  @Action(FetchMrtSpecificationsAction)
  setMrtSpecifications({ patchState }: StateContext<AssetManagementStateModel>) {
    return this.assetManagementService.getMRTSpecifications().pipe(
      tap((response) => {
        const mrtMap = new Map<string, MRT>();
        response.forEach((mrt) => {
          mrtMap.set(mrt.application, mrt);
        });
        patchState({ mrtSpecifications: mrtMap });
      })
    );
  }

  @Action(ResetAssetManagamentStateAction)
  resetCustomerStateAction({ patchState }: StateContext<AssetManagementStateModel>) {
    patchState(getDefaultState());
  }

  @Action(UpdateCurrentSpecificationAction)
  setCurrentSpecificationAction(
    { patchState }: StateContext<AssetManagementStateModel>,
    { specification }: UpdateCurrentSpecificationAction
  ) {
    patchState({ currentSpecification: specification });
  }

  @Action(FetchCurrentSpecificationAction)
  fetchCurrentSpecificationAction(
    { patchState, getState }: StateContext<AssetManagementStateModel>,
    { specificationId }: FetchCurrentSpecificationAction
  ) {
    patchState({ loading: true });
    const specification = getState().currentSpecification;
    if (specification && specification.id === specificationId) {
      patchState({ loading: false });
    } else {
      return this.assetManagementService.getSpecification(specificationId).pipe(
        tap((res) => {
          const specification = res.data;
          prepareSpecificationLineLabels(specification);
          patchState({ loading: false, currentSpecification: specification });
        }),
        catchError((error) => {
          console.error(error);
          patchState({ loading: false });
          return of(null);
        })
      );
    }
  }

  @Action(UpdateCurrentPaginatedSpecificationsAction)
  setCurrentPaginatedSpecificationsAction(
    { patchState }: StateContext<AssetManagementStateModel>,
    { specifications }: UpdateCurrentPaginatedSpecificationsAction
  ) {
    patchState({ currentPaginatedSpecifications: specifications });
  }

  @Selector()
  static state(state: AssetManagementStateModel) {
    return state;
  }

  @Selector()
  static editing(state: AssetManagementStateModel) {
    return state.editing;
  }

  @Selector()
  static specificationsMap(state: AssetManagementStateModel) {
    return state.specificationsMap;
  }

  @Selector()
  static currentSpecificationId(state: AssetManagementStateModel) {
    return state.currentSpecificationId;
  }

  @Selector()
  static currentCustomerCode(state: AssetManagementStateModel) {
    return state.currentCustomerCode;
  }

  @Selector()
  static isDefault(state: AssetManagementStateModel) {
    return state.specificationsMap.get(state.currentSpecificationId).defaultSpec;
  }

  @Selector()
  static mrtSpecifications(state: AssetManagementStateModel) {
    return state.mrtSpecifications;
  }

  @Selector()
  static currentSpecification(state: AssetManagementStateModel) {
    return state.currentSpecification;
  }

  @Selector()
  static currentPaginatedSpecifications(state: AssetManagementStateModel) {
    return state.currentPaginatedSpecifications;
  }

  @Selector()
  static loading(state: AssetManagementStateModel) {
    return state.loading;
  }

  private processSpecifications(specifications: SpecLine[]) {
    const labelsMap = new Map<string, number>();

    specifications.forEach((specLine) => {
      if (!specLine.defaultSpec) {
        const key = specLine.treads.join(', ');
        if (labelsMap.has(key)) {
          const newCount = labelsMap.get(key) + 1;
          labelsMap.set(key, newCount);
          specLine.label = `${key} (${newCount})`;
        } else {
          labelsMap.set(key, 0);
          specLine.label = `${key}`;
        }
      } else {
        specLine.label = specLine.type === SpecificationType.REPAIR_RETREAD ? 'Default RR' : 'Default RO';
      }
    });

    const sortedSpecfications = _.orderBy(specifications, ['type', 'label']);
    const defaultSpecs = _.remove(sortedSpecfications, (spec) => spec.defaultSpec);
    return [...sortedSpecfications, ...defaultSpecs];
  }
}
